type UserRoleDecision =
  | { type: 'continue' }
  | { type: 'redirect'; path: string }

interface UserRoleDecisionInput {
  routeName: unknown
  /** The page declares `definePageMeta({ permission })`. */
  declaresPermission: boolean
  isSuperUser: boolean
}

function isSystemAdminRoute(routeName: unknown) {
  return typeof routeName === 'string' && routeName.includes('system-admin')
}

function isOrganizationSettingsRoute(routeName: unknown) {
  return (
    typeof routeName === 'string' &&
    routeName.startsWith('organizationSlug-settings')
  )
}

/** The standalone question pages, `/:organizationSlug/:workspaceId/saq/...`. */
function isStandaloneQuestionRoute(routeName: unknown) {
  return (
    typeof routeName === 'string' &&
    routeName.startsWith('organizationSlug-workspaceId-saq')
  )
}

/** Whether 06.user-role has anything to decide on this route. */
export function isSuperadminGatedRoute(routeName: unknown): boolean {
  return (
    isSystemAdminRoute(routeName) ||
    isOrganizationSettingsRoute(routeName) ||
    isStandaloneQuestionRoute(routeName)
  )
}

/**
 * Decide the Superadmin-only verdict for a route. Pure: the middleware
 * (~/middleware/06.user-role.global.ts) loads the user, then calls this.
 *
 * - System admin and the standalone question pages are Superadmin-only.
 * - An organisation settings page that declares a permission is left to
 *   07.permission, which opens it to Owners and Org admins as their rules
 *   allow. One that declares none stays Superadmin-only, so a new page added
 *   without one is closed rather than open to every member. Guarded by route
 *   name (`…-settings-users`, `…-settings-user-userId`, etc.), so a member
 *   can't reach a sub-page by direct URL.
 */
export function decideUserRoleAction(
  input: UserRoleDecisionInput,
): UserRoleDecision {
  if (input.isSuperUser) return { type: 'continue' }

  if (
    isSystemAdminRoute(input.routeName) ||
    isStandaloneQuestionRoute(input.routeName)
  ) {
    return { type: 'redirect', path: '/403' }
  }

  if (
    isOrganizationSettingsRoute(input.routeName) &&
    !input.declaresPermission
  ) {
    return { type: 'redirect', path: '/403' }
  }

  return { type: 'continue' }
}
